import React, { useState } from "react";

import { Code2, FileImage, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import DraftsRevokeModal from "../../../common/DraftsRevokeModal/DraftsRevokeModal";
import ToolTip from "../../../common/ToolTip/ToolTip";
import {
  isHtmlTemplate,
  isLegacyTemplate,
  isSvgTemplate,
} from "../../../configs/templateTypes";
import { ReactComponent as EditIcon } from "../../../resources/icons/edit.svg";
import { ReactComponent as FlyingMail } from "../../../resources/icons/flying-mail.svg";
import { ReactComponent as CheckIcon } from "../../../resources/icons/succuss-tick.svg";
import useNavigationStore from "../../../store/navigationStore";
import HandleImgError from "../../../utils/HandleURLImgError";

const DraftsCredentialTemplateCard = (props) => {
  const {
    name,
    description,
    credential_template_id,
    issuer_credential_template_id,
    thumbnail_image,
    thumbnail_webp_url,
    navigate_url,
    removeCredentialFromList,
    removeActionButtons,
    isSelected,
    onSelect,
    template_type,
    programme_name,
    batch_name,
    programme_code,
    batch_code,
    fullWidth,
  } = props;

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(null);

  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme,
  );
  const currentBatch = useNavigationStore((state) => state.currentBatch);

  //! Can be used for deleting draft in future
  // const toggleModal = (id) => {
  //   setIsModalOpen(id);
  // };

  const handleIssueCertificateClick = (e) => {
    e.stopPropagation();

    if (isLegacyTemplate(template_type)) {
      const programme = currentProgramme || {
        programme_name: programme_name,
        programme_code: programme_code,
      };

      const batch = currentBatch || {
        batch_name: batch_name,
        batch_code: batch_code,
      };

      useNavigationStore.setState({
        currentProgramme: programme,
        currentBatch: batch,
      });

      const safeName = encodeURIComponent(name ?? "");
      navigate(
        `/issuer/workspace/batches/legacy-issuance/${credential_template_id}/${issuer_credential_template_id}/${safeName}/${template_type}`,
        {
          state: {
            programme: programme,
            batch: batch,
            fromDrafts: true,
          },
        },
      );
    } else if (isSvgTemplate(template_type) || isHtmlTemplate(template_type)) {
      navigate(
        `/issuer/certificate/issue/${credential_template_id}/${issuer_credential_template_id}/${programme_code}`,
      );
    }
  };

  const cardKey =
    issuer_credential_template_id ?? credential_template_id ?? "unknown";

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (credential_template_id && navigate_url) {
      navigate(`${navigate_url}`);
    }
  };

  const isCardInteractive = Boolean(
    onSelect || (credential_template_id && navigate_url),
  );
  const handleCardActivate = () => {
    if (onSelect) {
      onSelect(issuer_credential_template_id, {
        credential_template_id,
        template_type,
        issuer_credential_template_id,
      });
    } else if (credential_template_id && navigate_url) {
      navigate(`${navigate_url}`);
    }
  };
  const cardInteractionProps = isCardInteractive
    ? {
        role: "button",
        tabIndex: 0,
        onClick: handleCardActivate,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleCardActivate();
          }
        },
      }
    : {};

  return (
    <div
      id={`drafts-credential-template-card-${cardKey}`}
      className={`flex flex-col gap-3 bg-cfSecondary ${
        isSelected
          ? "border-2 border-dashed border-cfPrimaryButton scale-105"
          : "border border-cfCardBorder"
      } ${
        isCardInteractive &&
        "cursor-pointer hover:bg-cfCardHover hover:scale-105"
      } transition-transform duration-500 rounded-2xl overflow-hidden ${
        fullWidth ? "w-full" : "w-[19.125rem]"
      } relative`}
      name={credential_template_id}
      {...cardInteractionProps}
    >
      {isSelected && (
        <div
          id={`drafts-credential-template-card-selected-${cardKey}`}
          className="absolute top-2 right-2 flex items-center justify-center z-10"
        >
          <CheckIcon className="text-cfSecondary w-9 h-9" />
        </div>
      )}
      <div
        className={`flex flex-col ${
          removeActionButtons ? "h-[22rem]" : "h-full"
        } `}
      >
        <div className="flex flex-col h-fit">
          <div
            className={`flex items-center h-[12.5rem] border-b w-full bg-cfSecondary ${
              !thumbnail_image &&
              !thumbnail_webp_url &&
              "w-full animate-pulse bg-zinc-300"
            }`}
          >
            <img
              src={thumbnail_image ? thumbnail_image : thumbnail_webp_url}
              onError={(e) =>
                HandleImgError(e, thumbnail_image || thumbnail_webp_url)
              }
              alt=""
              className="w-full h-full object-cover object-top"
            />
          </div>

          {template_type && (
            <span
              className={`text-xs px-2 py-1 rounded-full shrink-0 absolute top-3 right-2 ${
                isSvgTemplate(template_type) || isHtmlTemplate(template_type)
                  ? "bg-[#EEF2FF] text-[#4F46E5]"
                  : "bg-[#ffe8cb] text-[#de881f]"
              }`}
            >
              {isHtmlTemplate(template_type) ? (
                <div className="flex items-center gap-1">
                  <Code2 className="h-3 w-3 text-cfPrimaryButton" />
                  <span>HTML</span>
                </div>
              ) : isSvgTemplate(template_type) ? (
                <div className="flex items-center gap-1">
                  <FileText className="h-3 w-3 text-cfPrimaryButton" />
                  <span>SVG</span>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <FileImage className="h-3 w-3 text-cfIssuedCredentialTemplateCardStatusUnderReviewBG" />
                  <span>Legacy</span>
                </div>
              )}
            </span>
          )}
        </div>

        <div
          className={`flex flex-col gap-4 ${
            !removeActionButtons ? "h-[7rem]" : "h-[4rem]"
          } m-4`}
        >
          <div>
            <div
              className={`${
                removeActionButtons
                  ? "h-10 overflow-ellipsis font-medium text-lg"
                  : "text-cfHeadingText font-semibold text-xl"
              } `}
            >
              <p
                className={`line-clamp-1 ${
                  !name && "h-8 w-full animate-pulse bg-zinc-300 rounded-md"
                }`}
              >
                {name}
              </p>
            </div>
            <div
              className={`${removeActionButtons ? "flex flex-col gap-5" : ""}`}
            >
              <p
                className={`${
                  removeActionButtons ? "text-xs" : "text-base"
                } line-clamp-2 ${
                  !description &&
                  "h-8 w-full animate-pulse bg-zinc-300 text-gray-400 rounded-md"
                }`}
              >
                {description}
              </p>

              {removeActionButtons && (
                <div className="flex gap-2">
                  {programme_name && (
                    <ToolTip
                      component={
                        <p
                          className="text-xs bg-cfCardTagFill text-cfCardTagText font-medium 
                        group-hover:bg-cfSignInSignUpOrText group-hover:text-cfSecondary 
                        max-w-[130px] truncate px-2 py-1 rounded-full h-fit"
                        >
                          {programme_name}
                        </p>
                      }
                      message={programme_name}
                      tooltipPosition="top-[-3rem]"
                      bgColor="#98A2B3"
                      tooltipWidth="max-w-[200px]"
                    />
                  )}
                  {batch_name && (
                    <ToolTip
                      component={
                        <p
                          className="text-xs bg-cfCardTagFill text-cfCardTagText font-medium 
                        group-hover:bg-cfSignInSignUpOrText group-hover:text-cfSecondary 
                        max-w-[130px] truncate px-2 py-1 rounded-full h-fit"
                        >
                          {batch_name}
                        </p>
                      }
                      message={batch_name}
                      tooltipPosition="top-[-3rem]"
                      bgColor="#98A2B3"
                      tooltipWidth="max-w-[200px]"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          {!removeActionButtons && (
            <div className="grid grid-cols-2 divide-x divide-slate-600 divide-solid">
              <div
                id={`drafts-credential-template-card-edit-${cardKey}`}
                className="flex justify-center items-center group"
                onClick={handleEditClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleEditClick(e);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={t("drafts:edit_template")}
              >
                <ToolTip
                  component={
                    <EditIcon
                      height={20}
                      width={20}
                      className="text-black hover:text-cfPrimaryLight transition-colors duration-500"
                    />
                  }
                  message={t("drafts:edit_template")}
                  tooltipPosition="top-[-3rem]"
                  bgColor="var(--cf-primary-color)"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
              <div
                id={`drafts-credential-template-card-issue-${cardKey}`}
                className="flex justify-center items-center group"
                onClick={handleIssueCertificateClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleIssueCertificateClick(e);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={t("drafts:issue_certificate")}
              >
                <ToolTip
                  component={
                    <FlyingMail
                      height={20}
                      width={20}
                      className="text-black hover:text-cfPrimaryLight transition-colors duration-500"
                    />
                  }
                  message={t("drafts:issue_certificate")}
                  tooltipPosition="top-[-3rem]"
                  bgColor="var(--cf-primary-color)"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div>
              {/* Can be used for deleting draft in future */}
              {/* <div
                className="flex justify-center items-center group"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleModal(issuer_credential_template_id);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleModal(issuer_credential_template_id);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <ToolTip
                  component={
                    <DeleteIcon
                      className="fill-cfSecondary transition-colors duration-500 hover:fill-cfIssuedCredentialTemplateCardStatusRevokedBG"
                      height={20}
                      width={20}
                    />
                  }
                  message={t("drafts:delete_draft")}
                  tooltipPosition="top-[-3rem]"
                  bgColor="#DC2626"
                  tooltipWidth={"max-w-[130px]"}
                />
              </div> */}
            </div>
          )}

          {/* <div className="flex gap-1"> //! Can be used for tags in future
            {tagsList ? (
              tagsList.slice(0, 2).map((item, index) => (
                <p
                  key={index}
                  className=" text-xs bg-cfCardTagFill text-cfCardTagText font-medium group-hover:bg-cfTagFillHover group-hover:text-cfSecondary w-fit px-2 py-1 rounded-full h-fit"
                >
                  {item}
                </p>
              ))
            ) : (
              <>
                <p className="h-5 w-4/12 animate-pulse text-xs bg-zinc-300 px-2 py-1 rounded-full"></p>
                <p className="h-5 w-5/12 animate-pulse text-xs bg-zinc-300 px-2 py-1 rounded-full"></p>
              </>
            )}
          </div> */}
        </div>
      </div>
      <DraftsRevokeModal
        isOpen={isModalOpen !== null}
        onClose={() => setIsModalOpen(null)}
        onConfirm={() => {
          removeCredentialFromList(isModalOpen);
          setIsModalOpen(null);
        }}
        title={t("drafts:delete_draft")}
        description={t("drafts:confirm_delete_draft")}
        confirmButtonText={t("drafts:delete")}
        cancelButtonText={t("drafts:cancel")}
      />
    </div>
  );
};
export default DraftsCredentialTemplateCard;
